import api from './api'

/**
 * Servicio para manejar las operaciones relacionadas con programas
 */
class ProgramsService {
    /**
     * Obtiene todos los programas
     * @returns {Promise<Array>} Lista de programas
     */
    async getPrograms() {
        try {
            const response = await api.get('/programs')
            return response.data
        } catch (error) {
            console.error('Error fetching programs:', error)
            throw error
        }
    }

    /**
     * Método de prueba para verificar la respuesta de la API
     * @param {number|string} year - Año escolar (1-4) o 'e' para egresado
     */
    async testAPIResponse(year = 4) {
        try {
            const yearParam = year === 'e' ? 'e' : year.toString()
            
            const response = await api.get(`/programs-without-childrens?year=${encodeURIComponent(yearParam)}&defaults=true`)
            
            return response.data
        } catch (error) {
            console.error('Error en testAPIResponse:', error)
            throw error
        }
    }

    /**
     * Obtiene programas sin hijos (programas principales)
     * @param {number|string} year - Año escolar o 'e' para egresado
     * @returns {Promise<Array>} Lista de programas principales
     */
    async getProgramsWithoutChildren(year = 4) {
        try {
            // Convertir año a formato esperado por la API
            const yearParam = year === 'e' ? 'e' : year.toString()
            // Usar la API correcta: /programs-without-childrens con year y defaults=true
            const response = await api.get(`/programs-without-childrens?year=${encodeURIComponent(yearParam)}&defaults=true`)
            let programs = response.data || []
            
            // La API ya devuelve los programas con detalles incluidos, no necesitamos filtrar ni hacer llamadas adicionales
            // Solo asegurar que cada programa tenga la estructura básica esperada
            programs = programs.map(program => ({
                id: program.id,
                name: program.name,
                Metas: program.Metas || { name: 'Sin modalidad' },
                Details: program.Details || [],
                parentId: program.parentId
            }))
            
            return programs
        } catch (error) {
            console.error('Error fetching programs without children:', error)
            // Retornar datos de fallback si la API falla
            return this.getFallbackPrograms(year)
        }
    }

    /**
     * Datos de fallback para cuando la API no está disponible
     * @param {number|string} year - Año escolar
     * @returns {Array} Lista de programas de fallback
     */
    getFallbackPrograms(year) {
        const yearLabel = year === 'e' ? 'Egresado' : `${year}° Medio`
        
        return [
            {
                id: 1,
                name: `PDV Presencial ${yearLabel}`,
                Metas: { name: 'Presencial' },
                Details: [
                    {
                        id: 1,
                        name: 'Modalidad',
                        main: true,
                        order: 1,
                        ProgramDetail: { value: 'Presencial' }
                    },
                    {
                        id: 2,
                        name: 'Horarios',
                        main: false,
                        order: 2,
                        ProgramDetail: { value: 'Flexibles' }
                    }
                ]
            },
            {
                id: 2,
                name: `PDV Online ${yearLabel}`,
                Metas: { name: 'Online' },
                Details: [
                    {
                        id: 3,
                        name: 'Modalidad',
                        main: true,
                        order: 1,
                        ProgramDetail: { value: 'Online' }
                    },
                    {
                        id: 4,
                        name: 'Plataforma',
                        main: false,
                        order: 2,
                        ProgramDetail: { value: 'Zoom' }
                    }
                ]
            }
        ]
    }

    /**
     * Obtiene programas con ubicaciones usando la API específica
     * @param {number} programId - ID del programa (ej: 367)
     * @returns {Promise<Array>} Lista de programas con sus ubicaciones
     */
    async getProgramsWithLocations(programId = 367) {
        try {
            const response = await api.get(`/programs/${programId}/withchildrens`)
            return response.data
        } catch (error) {
            console.error('Error fetching programs with locations:', error)
            throw error
        }
    }

    /**
     * Obtiene las comunas habilitadas para un programa específico
     * @param {string} programa - Código del programa (ej: 'Presencial')
     * @param {string} year - Año del programa (ej: '4')
     * @returns {Promise<Array>} Lista de programas con sus ubicaciones habilitadas
     */
    async getComunasByProgram(programa = 'Presencial', year = '4') {
        try {
            const response = await api.get(`/programs/meta/${programa}?year=${year}`)
            return response.data
        } catch (error) {
            console.error('Error fetching comunas by program:', error)
            throw error
        }
    }

    /**
     * Obtiene los meses disponibles para un programa específico
     * @param {string} programa - Código del programa (ej: 'Presencial')
     * @param {string} year - Año del programa (ej: '4')
     * @returns {Promise<Array>} Lista de meses disponibles
     */
    async getAvailableMonths(programa = 'Presencial', year = '4') {
        try {
            const response = await api.get(`/programs/meta/${programa}?year=${year}`)
            
            // Verificar si hay datos
            if (!response.data || response.data.length === 0) {
                console.warn('No hay datos en la respuesta de la API')
                return []
            }
            
            // Verificar si el programa tiene datos de meses disponibles
            const program = response.data[0]
            if (!program.enable && (!program.Metas || !program.Metas.meses_enable)) {
                console.warn('El programa no tiene datos de meses habilitados')
                return []
            }
            
            return await this.parseAvailableMonths(response.data)
        } catch (error) {
            console.error('Error fetching available months:', error)
            throw error
        }
    }

    /**
     * Obtiene los meses disponibles para una sede específica
     * @param {string} programa - Código del programa (ej: 'Presencial')
     * @param {string} year - Año del programa (ej: '4')
     * @param {string} sedeId - ID de la sede seleccionada
     * @returns {Promise<Array>} Lista de meses disponibles para la sede
     */
    async getAvailableMonthsBySede(programa, year, sedeId) {
        try {
            // Primero obtenemos los datos del programa
            const response = await api.get(`/programs/meta/${programa}?year=${year}`)
            const programData = response.data
            
            // Luego obtenemos los meses específicos para la sede
            const sedeMonths = this.getSedeMonths(sedeId, programData)
            
            return sedeMonths
        } catch (error) {
            console.error('Error fetching available months by sede:', error)
            throw error
        }
    }

    /**
     * Obtiene los meses disponibles a partir del PROGRAMA seleccionado.
     *
     * Es la vía correcta para resolver el mes de inicio: cada programa tiene su
     * propia Meta con su propio `meses_enable`. Resolver por nombre de modalidad
     * (getAvailableMonthsBySede) no distingue entre programas que comparten
     * modalidad. Ejemplo real: 'Tradicional Egresado' (meta 9 'Presencial', abril)
     * y 'Tradicional Egresado Invierno' (meta 28 'Intensivo Invierno', marzo)
     * son ambos Presencial, pero tienen meses distintos.
     *
     * @param {string|number} programId - ID del programa seleccionado
     * @returns {Promise<Array>} Lista de meses disponibles para ese programa
     */
    async getAvailableMonthsByProgram(programId) {
        if (!programId) return []

        try {
            const response = await api.get(`/programs/${programId}/withchildrens`)
            const raw = Array.isArray(response.data) ? response.data : [response.data]

            // withchildrens devuelve el programa junto a sus hijos: hay que tomar
            // exactamente el solicitado, no el primero del array.
            const program = raw.find(p => p && String(p.id) === String(programId))

            if (!program) return []
            if (!program.enable && !program.Metas?.meses_enable) {
                console.warn('El programa no tiene meses configurados:', programId)
                return []
            }

            return await this.parseAvailableMonths([program])
        } catch (error) {
            console.error('Error fetching available months by program:', error)
            return []
        }
    }

    /**
     * Obtiene los meses disponibles para una sede específica desde los datos del programa
     * @param {string} sedeId - ID de la sede
     * @param {Array} programData - Datos del programa
     * @returns {Promise<Array>} Lista de meses disponibles para la sede
     */
    async getSedeMonths(sedeId, programData) {
        if (!programData || programData.length === 0) {
            return []
        }
        
        const program = programData[0]
        
        // Verificar si hay datos de meses disponibles
        if (!program.enable && (!program.Metas || !program.Metas.meses_enable)) {
            console.warn('No se encontraron datos de meses habilitados para la sede')
            return []
        }
        
        // Por ahora, siempre usar la configuración general del programa
        // En el futuro, aquí se podría agregar lógica específica para cada sede
        const availableMonths = await this.parseAvailableMonths(programData)
        return availableMonths
    }

    /**
     * Parsea los meses disponibles desde los datos del programa
     * @param {Array} programData - Datos del programa
     * @returns {Promise<Array>} Lista de meses disponibles
     */
    async parseAvailableMonths(programData) {
        if (!programData || programData.length === 0) {
            return []
        }
        
        const program = programData[0]
        
        // Intentar diferentes estructuras de datos para encontrar los meses habilitados
        let meses = []
        let year = ''
        
        try {
            // Primero intentar con la estructura enable directa
            if (program.enable) {
                meses = JSON.parse(program.enable)
            }
            // Si no existe enable, intentar con meses_enable
            else if (program.Metas && program.Metas.meses_enable) {
                const mesesData = JSON.parse(program.Metas.meses_enable)
                if (mesesData.meses) {
                    meses = JSON.parse(mesesData.meses)
                    year = mesesData.id
                } else {
                    meses = mesesData
                }
            }
            else {
                return []
            }
            
            const monthNames = [
                'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
            ]
            
            // Obtener las fechas específicas desde la API de fechas
            let datesData = null
            try {
                const datesResponse = await api.get(`/datesprograms/${year}`)
                datesData = datesResponse.data[0] // Tomar el primer elemento del array
            } catch (error) {
                console.warn('No se pudieron obtener las fechas específicas:', error)
            }
            
            const availableMonths = []
            meses.forEach((mes, index) => {
                // Verificar si el mes está habilitado (value: true)
                const isEnabled = mes.value === true || mes.value === "true"
                
                if (isEnabled) {
                    const monthName = monthNames[index]
                    
                    // Obtener el día específico desde la API de fechas
                    let startDay = null
                    let startDate = null
                    
                    if (datesData) {
                        const monthKey = monthNames[index].toLowerCase()
                        if (datesData[monthKey]) {
                            const date = new Date(datesData[monthKey])
                            startDay = date.getDate()
                            startDate = `${startDay} de ${monthName.toLowerCase()}`
                        }
                    }
                    
                    availableMonths.push({
                        id: `${monthName.toLowerCase()}-${year}`,
                        name: `${monthName} ${year}`,
                        subtitle: `Inicio en ${monthName.toLowerCase()}`,
                        monthIndex: index,
                        year: year,
                        monthNumber: index + 1, // Para usar en APIs que requieren 1-12
                        startDay: startDay,
                        startDate: startDate
                    })
                }
            })
            
            return availableMonths
            
        } catch (error) {
            console.error('Error parsing meses habilitados:', error)
            console.error('Datos del programa:', program)
            return []
        }
    }



    /**
     * Obtiene los cursos disponibles para un mes específico
     * @param {string} programId - ID del programa
     * @param {string} locationId - ID de la ubicación
     * @param {number} month - Número del mes (1-12)
     * @returns {Promise<Array>} Lista de cursos disponibles
     */
    async getCoursesByMonth(programId, locationId, month) {
        try {
            const response = await api.get(`/courses/findbymes/programId?programId=${programId}&locationId=${locationId}&mes=${month}`)
            return response.data
        } catch (error) {
            console.error('Error fetching courses by month:', error)
            throw error
        }
    }

    /**
     * Obtiene todos los cursos con sus horarios para un programa y ubicación
     * @param {string} programId - ID del programa
     * @param {string} locationId - ID de la ubicación
     * @returns {Promise<Array>} Lista de cursos con sus horarios
     */
    async getCoursesWithSchedules(programId, locationId) {
        try {
            const response = await api.get(`/courses?programId=${programId}&locationId=${locationId}`)
            return response.data
        } catch (error) {
            console.error('Error fetching courses with schedules:', error)
            throw error
        }
    }

    /**
     * Procesa los cursos de la API y los convierte al formato necesario para el componente
     * @param {Array} coursesData - Datos de cursos desde la API
     * @returns {Object} Objeto con cursos procesados para las columnas izquierda y derecha
     */
    processCoursesForComponent(coursesData) {
        if (!coursesData || coursesData.length === 0) {
            return { leftColumn: [], rightColumn: [] }
        }

        // Agrupar cursos por materia
        const coursesBySubject = {}
        coursesData.forEach(course => {
            const subjectName = course.Subject ? course.Subject.name : 'Sin materia'
            if (!coursesBySubject[subjectName]) {
                coursesBySubject[subjectName] = []
            }
            coursesBySubject[subjectName].push(course)
        })
        // Convertir a formato del componente
        const processedCourses = Object.keys(coursesBySubject).map(subjectName => {
            const courses = coursesBySubject[subjectName]
            
            // Agrupar horarios por días de la semana
            const schedulesByDays = {}
            courses.forEach(course => {
                if (course.Schedules && course.Schedules.length > 0) {
                    course.Schedules.forEach(schedule => {
                        const dayKey = this.getDayKey(schedule.dayOfWeek)
                        if (!schedulesByDays[dayKey]) {
                            schedulesByDays[dayKey] = []
                        }
                        schedulesByDays[dayKey].push({
                            courseId: course.id,
                            courseName: course.name,
                            schedule: schedule,
                            timeSlot: `${schedule.startTime.slice(0, 5)} - ${schedule.endTime.slice(0, 5)}`
                        })
                    })
                }
            })
            

            // Crear grupos de días (puede estar vacío si no hay horarios)
            const dayGroups = this.createDayGroups(schedulesByDays)
            // Filtrar dayGroups inválidos
            const validDayGroups = dayGroups.filter(dg => dg && dg.timeSlots && Array.isArray(dg.weekDays) && dg.weekDays.length > 0);

            // Asegurar que cada dayGroup tenga weekDays como array
            validDayGroups.forEach(dayGroup => {
                if (!dayGroup.weekDays || !Array.isArray(dayGroup.weekDays)) {
                    dayGroup.weekDays = [];
                }
            });

            return {
                id: subjectName.toLowerCase().replace(/\s+/g, '-'),
                name: subjectName,
                availableDays: validDayGroups, // Puede estar vacío si no hay horarios
                courses: courses
            }
        })

        // Dividir en columnas (izquierda y derecha)
        const midPoint = Math.ceil(processedCourses.length / 2)
        const result = {
            leftColumn: processedCourses.slice(0, midPoint),
            rightColumn: processedCourses.slice(midPoint)
        }
        
        return result
    }

    /**
     * Convierte el nombre del día a una clave estándar
     * @param {string} dayName - Nombre del día en español
     * @returns {string} Clave del día
     */
    getDayKey(dayName) {
        const dayMap = {
            'Lunes': 'monday',
            'Martes': 'tuesday',
            'Miércoles': 'wednesday',
            'Jueves': 'thursday',
            'Viernes': 'friday',
            'Sábado': 'saturday',
            'Domingo': 'sunday'
        }
        return dayMap[dayName] || dayName.toLowerCase()
    }

    /**
     * Crea grupos de días para mostrar en el componente
     * @param {Object} schedulesByDays - Horarios agrupados por día
     * @returns {Array} Grupos de días con sus horarios
     */
    createDayGroups(schedulesByDays) {
        const dayGroups = []

        // Crear grupos de días combinados (ej: Lunes y Miércoles)
        const dayCombinations = [
            { key: 'lunes-miercoles', days: ['monday', 'wednesday'], name: 'Lunes y Miércoles' },
            { key: 'martes-jueves', days: ['tuesday', 'thursday'], name: 'Martes y Jueves' },
            { key: 'viernes', days: ['friday'], name: 'Viernes' },
            { key: 'sabado', days: ['saturday'], name: 'Sábado' },
            { key: 'lunes', days: ['monday'], name: 'Lunes' },
            { key: 'martes', days: ['tuesday'], name: 'Martes' },
            { key: 'miercoles', days: ['wednesday'], name: 'Miércoles' },
            { key: 'jueves', days: ['thursday'], name: 'Jueves' }
        ]

        dayCombinations.forEach(combination => {
            const timeSlots = []
            const weekDays = []

            combination.days.forEach(day => {
                if (schedulesByDays[day]) {
                    schedulesByDays[day].forEach(schedule => {
                        // Crear un ID único para el time slot
                        const timeSlotId = `${combination.key}-${schedule.timeSlot.replace(/[:\s-]/g, '-')}`
                        
                        const timeSlot = {
                            id: timeSlotId,
                            time: schedule.timeSlot,
                            courseId: schedule.courseId,
                            courseName: schedule.courseName,
                            schedule: schedule.schedule
                        }
                        
                        // Evitar duplicados
                        const existingSlot = timeSlots.find(slot => slot.time === schedule.timeSlot)
                        if (!existingSlot) {
                            timeSlots.push(timeSlot)
                        }
                        
                        if (!weekDays.includes(day)) {
                            weekDays.push(day)
                        }
                    })
                }
            })

            if (timeSlots.length > 0) {
                const dayGroup = {
                    id: combination.key,
                    name: combination.name,
                    weekDays: weekDays, // Asegurar que siempre sea un array
                    timeSlots: timeSlots
                }
                
                // Solo agregar si weekDays es válido y no está vacío
                if (Array.isArray(weekDays) && weekDays.length > 0) {
                    dayGroups.push(dayGroup)
                } else {
                    console.warn(`Omitiendo grupo de días ${combination.name} - weekDays inválido:`, weekDays);
                }
            }
        })

        return dayGroups
    }


}

export default new ProgramsService() 